import React, { useState, useEffect, useRef, useMemo, version } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { auth } from "../../firebase.js";
import ReactQuill, { Quill } from "react-quill-new";
import { FONT_TOOLBAR_ENTRY } from "../../utils/quillFonts";
import ImageResize from "quill-image-resize-module-react";
import QuillTableBetter from "quill-table-better";
import Select from "react-select";
import Tesseract from "tesseract.js";
import * as pdfjsLib from "pdfjs-dist";
import WorkspaceSidebar from "./WorkspaceSidebar.js";
import "quill-better-table/dist/quill-better-table.css";
import "react-quill-new/dist/quill.snow.css";
import "react-quill/dist/quill.snow.css";
import "../../css/EditDocument.css";
import "../../css/DocumentationMainPage.css"
import "../../css/ConflictResolver.css"
import { useContextMenu } from "../shared/ContextMenu";
import CommandCenterFilePickerModal from '../shared/CommandCenterFilePickerModal';
import { useQuickLinks } from "../../contexts/QuickLinksContext";
import "../../css/HomePage.css"
import "../../css/Create.css";
import "../../css/Account.css"
import "../../css/DocumentationHomeComponent.css";
import "../../css/QuillOverrides.css";
import ConflictResolver from "../documentation/ConflictResolver.js";
import useUnsavedChangesGuard from "../../utils/useUnsavedChangesGuard";
import useTemplateSnippets, { insertTemplateToolbarHandler, decorateInsertTemplateButton } from "../../utils/useTemplateSnippets";
import WorkspaceDocumentHistoryModal from "./WorkspaceDocumentHistoryModal.js";

// Task Reference
import "../shared/TaskReferenceBlot";
import TaskReferenceDropdown from "../shared/TaskReferenceDropdown";
import { useTaskReference } from "../../utils/useTaskReference";
import "../shared/UserMentionBlot";
import UserMentionDropdown from "../shared/UserMentionDropdown";

// Snapshot utilities
import { useAutoWorkspaceSnapshot } from "../../utils/teamWorkspaceSnapshotUtils";

import "../../css/TaskReference.css";
import "../../css/DocEditorMakeover.css";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";
Quill.register({ "modules/table-better": QuillTableBetter }, true);
Quill.register("modules/imageResize", ImageResize);
const Inline = Quill.import("blots/inline");

class InlineCode extends Inline {
  static create() {
    let node = super.create();
    node.setAttribute("class", "inline-code");
    return node;
  }

  static formats(node) {
    return true;
  }
}

InlineCode.blotName = "inline-code";
InlineCode.tagName = "code";

Quill.register(InlineCode);

function EditWorkspaceDocument() {
  const { customDomain, type, id, documentName } = useParams();
  const navigate = useNavigate();

  // Existing state variables
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [workspace, setWorkspace] = useState(null);
  const { enhancedRightClickEnabled } = useQuickLinks();
  const [document, setDocument] = useState(null);
  const [canEdit, setCanEdit] = useState(false);

  // Document content and metadata states
  const [name, setName] = useState("");
  const [content, setContent] = useState("");
  const [description, setDescription] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [showTableOfContents, setShowTableOfContents] = useState(true); // BBD-58 — spaces only
  const [desiredType, setDesiredType] = useState(type);

  // External view-only shares with people outside the workspace (BBD-60).
  // Managed live (add/revoke apply immediately), unlike the batched permissions.
  const [externalShares, setExternalShares] = useState([]);
  const [canManageExternalShares, setCanManageExternalShares] = useState(false);
  const [currentExternalEmail, setCurrentExternalEmail] = useState("");
  const [documentVersion, setDocumentVersion] = useState(1);
  const quillRef = useRef(null);
  // "Insert template" snippet picker (BBD-108)
  const templateSnippets = useTemplateSnippets(quillRef, { workspaceId: workspace?.id || null });
  const { taskRefProps, userMentionProps } = useTaskReference(quillRef, { mode: "workspace", workspaceId: workspace?.id });

  // History modal states
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [documentHistory, setDocumentHistory] = useState([]);

  // UI states
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConflictResolver, setShowConflictResolver] = useState(false);
  const [latestContent, setLatestContent] = useState("");
  const [loadingMessage, setLoadingMessage] = useState("Loading...");

  // Parent/relationship states
  const [selectedType, setSelectedType] = useState("");
  const [selectedParent, setSelectedParent] = useState("");
  const [allDocumentation, setAllDocumentation] = useState([]);

  // User states
  const [userUid, setUserUid] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [ownerIsSubscriber, setOwnerIsSubscriber] = useState(false)
  const [workspaceTheme, setWorkspaceTheme] = useState(null)
  const [authLoading, setAuthLoading] = useState(true);

  // Permission states
  const [emails, setEmails] = useState([]);
  const [currentEmail, setCurrentEmail] = useState("");

  // Split view states
  const [showSplitView, setShowSplitView] = useState(window.innerWidth > 1200);
  const [selectedViewDoc, setSelectedViewDoc] = useState(null);
  const [selectedViewDocName, setSelectedViewDocName] = useState("");
  const [viewDocContent, setViewDocContent] = useState("");
  const [viewDocTitle, setViewDocTitle] = useState("");

  // Attachment states
  const [attachmentFile, setAttachmentFile] = useState(null); // New file to upload
  const [ccPickerOpen, setCcPickerOpen] = useState(false);
  const [attachmentDescription, setAttachmentDescription] = useState("");
  const [currentAttachment, setCurrentAttachment] = useState(null); // Existing attachment from document

  // Snapshot state variables
  const [snapshotStatus, setSnapshotStatus] = useState('');
  const { snapshotFrequencyMinutes } = useQuickLinks();

  // Handle window resize for split view
  useEffect(() => {
    const handleResize = () => {
      setShowSplitView(window.innerWidth > 1200);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }); // Note: No dependency array here - this is intentional

  // Warn before leaving with unsaved changes (BBD-56) — covers tab close /
  // refresh, in-app navigation (sidebar links, navigate() calls), and the
  // browser Back/Forward buttons.
  const { releaseGuard } = useUnsavedChangesGuard(hasUnsavedChanges);

  


  // Quill editor modules configuration
  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [FONT_TOOLBAR_ENTRY],
          [{ header: 1 }, { header: 2 }, { header: 3 }, { header: 4 }],
          [{ size: ["small", false, "large"] }],
          ["bold", "italic", "underline", "strike"],
          [{ color: [] }, { background: [] }],
          [{ script: "sub" }, { script: "super" }],
          [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
          [{ indent: "-1" }, { indent: "+1" }],
          [{ align: [] }],
          ["blockquote", "code-block"],
          ["link", "image", "video"],
          ["inline-code"],
          ["formula"],
          ["table-better"], // table button
          ["clean"],
          ["image"],
          ["insert-template"], // Insert a snippet template (BBD-108)
          ["undo", "redo"], // Add undo and redo buttons
        ],
        handlers: {
          undo: function() {
            this.quill.history.undo();
          },
          redo: function() {
            this.quill.history.redo();
          },
          "insert-template": insertTemplateToolbarHandler
        }
      },
      history: {
        delay: 1000,
        maxStack: 100,
        userOnly: true
      },
      imageResize: {
        parchment: Quill.import("parchment"),
        modules: ["Resize", "DisplaySize"],
      },
      table: false,
      "table-better": {
        language: "en_US",
        menus: [
          "column",
          "row",
          "merge",
          "table",
          "cell",
          "wrap",
          "copy",
          "delete",
        ],
        toolbarTable: true,
        tableCreationAttributes: {
          className: 'responsive-table',
          style: 'width: 100%; max-width: 100%;'
        }
      },
      keyboard: {
        bindings: QuillTableBetter.keyboardBindings,
      },
    }),
    []
  );
  
  const PERMISSION_OPTIONS = [
    { value: "ADMIN", label: "Administrator" },
    { value: "EDITOR", label: "Editor" },
    { value: "VIEWER", label: "Viewer" },
  ];

  // Prepare document data for snapshots
  const documentData = {
    type: type.toLowerCase(),
    name: name,
    content: content,
    description: description,
    parentPage: type === 'page' ? selectedParent : undefined,
    isPublic: isPublic,
    completed: completed
  };

  // Auto-snapshot hook for team workspace documents
  const { startAutoSnapshot, stopAutoSnapshot, saveManualSnapshot } = useAutoWorkspaceSnapshot(
    workspace?.id,
    documentData,
    snapshotFrequencyMinutes,
    name && content && name.trim() !== '' && content.trim() !== '' // Only enable when we have name and content
  );

  // Add a new state to track when Firebase auth is ready
  const [authReady, setAuthReady] = useState(false);

  // Move the auth state listener to run before any data fetching
  useEffect(() => {
    setLoadingMessage('Checking authentication...');
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUserUid(user.uid);
        setUserEmail(user.email);
      } else {
        setUserUid(null);
        setUserEmail(null);
      }
      // Mark auth as ready regardless of whether user is logged in or not
      setAuthReady(true);
      setAuthLoading(false)
    });
    return () => unsubscribe();
  }, []);

  // Modify the workspace fetch to depend on authReady
  useEffect(() => {
    // Only run this effect if auth is ready and we have a customDomain
    if (!authReady || !customDomain) return;
    setLoadingMessage('Fetching workspace data...');

    const abortController = new AbortController();
    const signal = abortController.signal;

    const fetchWorkspaceByCustomDomain = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          setError("You must be logged in to view this document");
          setLoading(false);
          return;
        }

        const token = await user.getIdToken();

        // Pass the signal to the fetch request to make it abortable
        const response = await fetch(
          `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/domain/${customDomain}/`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "auth-Token": `Bearer ${token}`,
              "Authorization": `${token}`,
              "Requesting-User-Uid": user.uid,
            },
            signal: signal, // This allows the fetch to be aborted
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch workspace: ${response.status}`);
        }

        const workspaceData = await response.json();
        setWorkspace(workspaceData);
        setOwnerIsSubscriber(workspaceData.owner.is_subscriber)
        setWorkspaceTheme(workspaceData.theme)
      } catch (error) {
        // AbortError is expected when navigation occurs, so don't treat it as an error
        if (error.name !== "AbortError") {
          console.error("Error fetching workspace:", error);
          setError("Error fetching workspace details");
          setLoading(false);
        }
      }
    };

    fetchWorkspaceByCustomDomain();

    return () => {
      abortController.abort();
    };
  }, [authReady, customDomain]);

  useEffect(() => {
    // Skip processing if the URL is for create document page
    if (type === "create" && id === "document") {
      return;
    }

    const fetchDocumentDetails = async () => {
      if (!workspace || !workspace.id) return;
      setLoadingMessage('Fetching document data...');

      try {
        const user = auth.currentUser;
        if (!user) {
          setError("You must be logged in to view this document");
          setLoading(false);
          return;
        }

        const token = await user.getIdToken();
        let endpoint;

        // Determine the correct endpoint based on document type
        if (type === "space") {
          endpoint = `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/spaces/${id}/`;
        } else if (type === "page") {
          endpoint = `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/pages/${id}/`;
        } else {
          throw new Error(`Invalid document type: ${type}`);
        }

        const response = await fetch(endpoint, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "auth-Token": `Bearer ${token}`,
              "Authorization": `${token}`,
            "Requesting-User-Uid": user.uid,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch document: ${response.status}`);
        }

        const documentData = await response.json();

        // Determine if user can edit this document
        let canEditValue = false;

        // Use the user_role field directly from the workspace response
        if (
          workspace.user_role &&
          ["OWNER", "ADMIN", "EDITOR"].includes(workspace.user_role)
        ) {
          canEditValue = true;
        }

        // Set the state once after all conditions are evaluated
        setCanEdit(canEditValue);
        setDocument(documentData);
        setLoading(false);
      } catch (error) {
        setLoadingMessage('Error fetching document data.');
        console.error(`Error fetching ${type} details:`, error);
        setError(`Error fetching document details: ${error.message}`);
        setLoading(false);
      }
    };

    if (workspace && id && type) {
      fetchDocumentDetails();
    }
  }, [workspace, id, type]);

  // Populate form data once document is loaded
  useEffect(() => {
    if (!document) return;

    // Set the document properties based on type
    if (type === "space") {
      setName(document.name || "");
      setTimeout(() => {
        if (quillRef.current) {
          const editor = quillRef.current.getEditor();

          // Clean up the HTML first (remove <temporary> tags)
          let cleanHtml = document.content;
          cleanHtml = cleanHtml.replace(
            /<temporary[^>]*>.*?<\/temporary>/g,
            ""
          );

          // Convert cleaned HTML to Delta
          const delta = editor.clipboard.convert({ html: cleanHtml });

          // Get current cursor position (optional but nice for UX)
          const range = editor.getSelection();

          // Update editor content using Delta
          editor.updateContents(delta, "user");

          // Set cursor position properly (otherwise cursor will jump randomly)
          if (range) {
            editor.setSelection(
              delta.length() - (range.length || 0),
              0,
              "silent"
            );
          }

          editor.scrollSelectionIntoView();

          // Save clean HTML if needed (optional)
          setContent(editor.root.innerHTML);
        }
      }, 1000);
      setDescription(document.description || "");
      setIsPublic(document.is_public || false);
      setCompleted(document.completed || false);
      setShowTableOfContents(document.show_table_of_contents !== false);
      setDocumentVersion(document.version || 1);
      
      // Set attachment data if it exists
      if (document.attachment) {
        setCurrentAttachment(document.attachment);
        setAttachmentDescription(document.attachment.description || "");
      }
    } else if (type === "page") {
      setName(document.page_name || "");
      setTimeout(() => {
        if (quillRef.current) {
          const editor = quillRef.current.getEditor();

          // Clean up the HTML first (remove <temporary> tags)
          let cleanHtml = document.content;
          cleanHtml = cleanHtml.replace(
            /<temporary[^>]*>.*?<\/temporary>/g,
            ""
          );

          // Convert cleaned HTML to Delta
          const delta = editor.clipboard.convert({ html: cleanHtml });

          // Get current cursor position (optional but nice for UX)
          const range = editor.getSelection();

          // Update editor content using Delta
          editor.updateContents(delta, "user");

          // Set cursor position properly (otherwise cursor will jump randomly)
          if (range) {
            editor.setSelection(
              delta.length() - (range.length || 0),
              0,
              "silent"
            );
          }

          editor.scrollSelectionIntoView();

          // Save clean HTML if needed (optional)
          setContent(editor.root.innerHTML);
        }
      }, 1000);
      setDescription(document.description || "");
      setIsPublic(document.is_public || false);
      setCompleted(document.completed || false);
      setShowTableOfContents(document.show_table_of_contents !== false);
      setDocumentVersion(document.version || 1);
      
      // Set attachment data if it exists
      if (document.attachment) {
        setCurrentAttachment(document.attachment);
        setAttachmentDescription(document.attachment.description || "");
      }

      // Set parent reference
      if (document.space) {
        const parentSelection = {
          value: document.space.id,
          type: "space",
        };
        setSelectedParent(parentSelection);
      } else if (document.parent_page) {
        const parentSelection = {
          value: document.parent_page.id,
          type: "page",
        };
        setSelectedParent(parentSelection);
      }
    }
    setHasUnsavedChanges(false);
  }, [document, type]);

  // Fetch all spaces and pages for parent selection (for pages)
  useEffect(() => {
    const fetchWorkspaceDocumentation = async () => {
      if (!workspace || !workspace.id) return;
      setLoadingMessage('Fetching parent options...');

      try {
        const user = auth.currentUser;
        if (!user) return;

        const token = await user.getIdToken();

        // Fetch spaces and pages concurrently
        const [spacesResponse, pagesResponse] = await Promise.all([
          fetch(
            `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/spaces/`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                "auth-Token": `Bearer ${token}`,
              "Authorization": `${token}`,
                "Requesting-User-Uid": user.uid,
              },
            }
          ),
          fetch(
            `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/pages/`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                "auth-Token": `Bearer ${token}`,
              "Authorization": `${token}`,
                "Requesting-User-Uid": user.uid,
              },
            }
          ),
        ]);

        // Process spaces
        const spacesData =
          spacesResponse.status === 200 ? await spacesResponse.json() : [];
        const spaceOptions = Array.isArray(spacesData)
          ? spacesData.map((space) => ({
              label: `Space: ${space.name}`,
              value: space.id,
              type: "space",
            }))
          : [];

        // Process pages
        const pagesData =
          pagesResponse.status === 200 ? await pagesResponse.json() : [];
        // Handle both array format and object with pages property
        const pagesArray = Array.isArray(pagesData) ? pagesData : (pagesData.pages || []);
        const pageOptions = Array.isArray(pagesArray)
          ? pagesArray
              // Filter out the current page to avoid self-reference
              .filter((page) => page.id !== parseInt(id))
              .map((page) => ({
                label: `Page: ${page.page_name}`,
                value: page.id,
                type: "page",
              }))
          : [];

        // Combine and set all documentation
        const combinedOptions = [...spaceOptions, ...pageOptions];
        setAllDocumentation(combinedOptions);
      } catch (error) {
        console.error("Error fetching workspace documentation:", error);
      }
    };

    if (workspace) {
      fetchWorkspaceDocumentation();
    }
  }, [workspace, id, type]);

  // Fetch document permissions
  useEffect(() => {
    const fetchDocumentPermissions = async () => {
      if (!workspace || !workspace.id || !document) return;
      setLoadingMessage('Fetching document permissions...');

      try {
        const user = auth.currentUser;
        if (!user) return;

        const token = await user.getIdToken();

        const endpoint = `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/permissions/${type}/${id}/`;

        const response = await fetch(endpoint, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "auth-Token": `Bearer ${token}`,
              "Authorization": `${token}`,
            "Requesting-User-Uid": user.uid,
          },
        });

        if (response.ok) {
          const permissionsData = await response.json();
          // Format permissions for our UI
          const formattedPermissions = permissionsData.map((perm) => ({
            email: perm.user.email,
            role: perm.role,
          }));
          setEmails(formattedPermissions);
        }
      } catch (error) {
        console.error("Error fetching document permissions:", error);
      }
    };

    if (workspace && document) {
      fetchDocumentPermissions();
    }
  }, [workspace, document, id, type]);

  // Add this after the ReactQuill component in the return statement
  useEffect(() => {
    setLoadingMessage('Initializing editor...');
    if (quillRef.current) {
      // Get the toolbar element
      const toolbar = quillRef.current.getEditor().getModule('toolbar');
      
      // Find the undo and redo buttons and replace their content with Font Awesome icons
      const undoButton = toolbar.container.querySelector('.ql-undo');
      const redoButton = toolbar.container.querySelector('.ql-redo');
      
      if (undoButton) {
        undoButton.innerHTML = '<i class="fas fa-undo"></i>';
        undoButton.title = 'Undo';
      }
      
      if (redoButton) {
        redoButton.innerHTML = '<i class="fas fa-redo"></i>';
        redoButton.title = 'Redo';
      }
      decorateInsertTemplateButton(toolbar);
    }
  }, [quillRef.current]);

  // Auto-snapshot management
  useEffect(() => {
    if (workspace?.id && name && content) {
      startAutoSnapshot();
    } else {
      stopAutoSnapshot();
    }

    // Cleanup on unmount
    return () => stopAutoSnapshot();
  }, [workspace?.id, name, content, startAutoSnapshot, stopAutoSnapshot]);

  // Manual snapshot handler
  const handleManualSnapshot = async () => {
    setSnapshotStatus('saving');
    try {
      const success = await saveManualSnapshot();
      if (success) {
        setSnapshotStatus('success');
        setTimeout(() => setSnapshotStatus(''), 2000);
      } else {
        setSnapshotStatus('error');
        setTimeout(() => setSnapshotStatus(''), 3000);
      }
    } catch (error) {
      setSnapshotStatus('error');
      setTimeout(() => setSnapshotStatus(''), 3000);
    }
  };

  // Fetch selected view document content (for split view)
  useEffect(() => {
    const fetchViewDocument = async () => {
      if (!selectedViewDoc || !workspace || !workspace.id) return;
      setLoadingMessage('Fetching side view document...');

      try {
        const user = auth.currentUser;
        if (!user) return;

        const token = await user.getIdToken();

        const viewDocItem = allDocumentation.find(
          (item) => item.value === selectedViewDoc
        );
        if (!viewDocItem) return;

        let endpoint;
        if (viewDocItem.type === "space") {
          endpoint = `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/spaces/${selectedViewDoc}/`;
        } else {
          endpoint = `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/pages/${selectedViewDoc}/`;
        }

        const response = await fetch(endpoint, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "auth-Token": `Bearer ${token}`,
              "Authorization": `${token}`,
            "Requesting-User-Uid": user.uid,
          },
        });

        if (response.ok) {
          const viewDocData = await response.json();
          setViewDocContent(viewDocData.content || "");
          setViewDocTitle(
            viewDocData.name || viewDocData.page_name || "Document"
          );
        }
      } catch (error) {
        console.error("Error fetching view document:", error);
      }
    };

    if (selectedViewDoc) {
      fetchViewDocument();
    }
  }, [selectedViewDoc, workspace, allDocumentation]);

  // Add these handler functions to the component

  // Toggle fullscreen mode
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Handle content changes
  const handleContentChange = (value) => {
    setContent(value);
    setHasUnsavedChanges(true);
  };

  // Handle name changes
  const handleNameChange = (e) => {
    setName(e.target.value);
    setHasUnsavedChanges(true);
  };

  // Handle description changes
  const handleDescriptionChange = (e) => {
    setDescription(e.target.value);
    setHasUnsavedChanges(true);
  };

  // Handle public status toggle
  const handlePublicToggle = (e) => {
    setIsPublic(e.target.checked);
    setHasUnsavedChanges(true);
  };

  // Handle completed status toggle
  const handleCompletedToggle = (e) => {
    setCompleted(e.target.checked);
    setHasUnsavedChanges(true);
  };

  // Handle history button click
  const handleHistoryButtonClick = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        alert("You must be logged in to view document history");
        return;
      }

      const token = await user.getIdToken();
      
      // Determine the correct endpoint based on document type
      let endpoint;
      if (type === "space") {
        endpoint = `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/spaces/${id}/history/`;
      } else if (type === "page") {
        endpoint = `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/pages/${id}/history/`;
      } else {
        alert("Invalid document type");
        return;
      }

      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "auth-Token": `Bearer ${token}`,
          "Authorization": `${token}`,
          "Requesting-User-Uid": user.uid,
        },
      });

      if (response.ok) {
        const historyResponse = await response.json();
        // Extract the history array from the response
        const historyData = historyResponse.history || [];
        setDocumentHistory(historyData);
        setShowHistoryModal(true);
      } else {
        throw new Error(`Failed to fetch document history: ${response.status}`);
      }
    } catch (error) {
      console.error("Error fetching document history:", error);
      alert("Failed to fetch document history. Please try again.");
    }
  };

  // Handle parent selection (for pages)
  const handleParentSelection = (selected) => {
    setSelectedParent(selected);
    setHasUnsavedChanges(true);
  };

  // Add/remove collaborators
  const handleAddEmail = () => {
    if (currentEmail && !emails.some((item) => item.email === currentEmail)) {
      setEmails([...emails, { email: currentEmail, role: "EDITOR" }]);
      setCurrentEmail("");
      setHasUnsavedChanges(true);
    } else if (emails.some((item) => item.email === currentEmail)) {
      alert("This email has already been added.");
    } else {
      alert("Please enter a valid email address.");
    }
  };

  const handleRemoveEmail = (index) => {
    const updatedEmails = [...emails];
    updatedEmails.splice(index, 1);
    setEmails(updatedEmails);
    setHasUnsavedChanges(true);
  };

  const handleRoleChange = (index, newRole) => {
    const updatedEmails = [...emails];
    updatedEmails[index].role = newRole;
    setEmails(updatedEmails);
    setHasUnsavedChanges(true);
  };

  // ── External view-only sharing (BBD-60) ──────────────────────────────────
  // Shares with people outside the workspace apply immediately (they are not
  // batched with the save button): adding sends the email right away, and
  // revoking kills the emailed link right away.

  useEffect(() => {
    const fetchExternalShares = async () => {
      try {
        const user = auth.currentUser;
        if (!user || !workspace || !workspace.id) return;
        const token = await user.getIdToken();
        const response = await fetch(
          `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/external-shares/${type}/${id}/`,
          {
            headers: {
              "Content-Type": "application/json",
              "Auth-Token": `Bearer ${token}`,
              "Authorization": `${token}`,
              "Requesting-User-Uid": user.uid,
            },
          }
        );
        if (response.ok) {
          setExternalShares(await response.json());
          setCanManageExternalShares(true);
        } else {
          // 403 → this member can't manage sharing; hide the section
          setCanManageExternalShares(false);
        }
      } catch (error) {
        console.error("Error fetching external shares:", error);
      }
    };

    if (workspace && id && type) {
      fetchExternalShares();
    }
  }, [workspace, id, type]);

  const handleAddExternalShare = async () => {
    const email = currentExternalEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) {
      alert("Please enter a valid email address.");
      return;
    }
    if (externalShares.some((share) => share.email === email)) {
      alert("This document is already shared with that email.");
      return;
    }
    try {
      const user = auth.currentUser;
      if (!user) return;
      const token = await user.getIdToken();
      const response = await fetch(
        `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/external-shares/create/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Auth-Token": `Bearer ${token}`,
            "Authorization": `${token}`,
            "Requesting-User-Uid": user.uid,
          },
          body: JSON.stringify({ document_type: type, document_id: id, email }),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to share the document.");
      }
      setExternalShares([...externalShares, data]);
      setCurrentExternalEmail("");
    } catch (error) {
      alert(error.message);
    }
  };

  const handleRemoveExternalShare = async (share) => {
    if (!window.confirm(`Revoke ${share.email}'s access? Their emailed link will stop working immediately.`)) {
      return;
    }
    try {
      const user = auth.currentUser;
      if (!user) return;
      const token = await user.getIdToken();
      const response = await fetch(
        `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/external-shares/${share.id}/delete/`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "Auth-Token": `Bearer ${token}`,
            "Authorization": `${token}`,
            "Requesting-User-Uid": user.uid,
          },
        }
      );
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to revoke the share.");
      }
      setExternalShares(externalShares.filter((s) => s.id !== share.id));
    } catch (error) {
      alert(error.message);
    }
  };

    // Attachment handling functions
    const handleAttachmentUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            // List of allowed extensions
            const allowedExtensions = ['.zip', '.pdf', '.mp3', '.mp4', '.png', '.jpeg'];

            // Get the file extension including the dot
            const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;

            // Check if the file extension is in the allowed list
            if (!allowedExtensions.includes(fileExtension)) {
            alert('Please select a ZIP, PDF, MP3, MP4, PNG, or JPEG file.');
            event.target.value = ''; // Clear the input
            return;
            }

            // Validate file size (8MB limit)
            const maxSize = 8 * 1024 * 1024; // 8MB in bytes
            if (file.size > maxSize) {
            alert('File size must be less than 8MB.');
            event.target.value = ''; // Clear the input
            return;
            }

            setAttachmentFile(file);
            setHasUnsavedChanges(true);
        }
    };

  const removeAttachment = () => {
    setAttachmentFile(null);
    setAttachmentDescription("");
    // Clear the file input
    const fileInput = document.getElementById('attachment-upload');
    if (fileInput) {
      fileInput.value = '';
    }
    setHasUnsavedChanges(true);
  };

  const removeCurrentAttachment = () => {
    setCurrentAttachment(null);
    setAttachmentDescription("");
    setHasUnsavedChanges(true);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const truncateFileName = (fileName, maxLength = 40) => {
    if (fileName.length <= maxLength) {
      return fileName;
    }
    const extension = fileName.split('.').pop();
    const name = fileName.substring(0, fileName.lastIndexOf('.'));
    const truncatedName = name.substring(0, maxLength - extension.length - 4) + '...';
    return truncatedName + '.' + extension;
  };

  const handleSubmit = async () => {
    // Validation checks
    if (!name || name.trim() === "") {
      alert("Please enter a name for your document.");
      return;
    }

    // Check that name only contains letters and numbers
    if (!/^[a-zA-Z0-9\s\.\-\_]+$/.test(name)) {
      alert(
        "Document name must contain only letters, numbers, spaces, periods, dashes, and underscores."
      );
      return;
    }

    // Duplicate name check (ignore current document, ignore label prefixes, case-insensitive)
    const nameExists = allDocumentation.some(doc => {
      // Remove label prefix ("Space: ", "Page: ") if present
      const labelText = (doc.label || "").replace(/^(Space|Page):\s*/, "");
      // Exclude the current document (by id)
      if (doc.value === parseInt(id)) return false;
      return labelText.trim().toLowerCase() === name.trim().toLowerCase();
    });
    if (nameExists) {
      alert("A document with this name already exists in this workspace. Please choose a different name.");
      return;
    }

    // Check for parent document if converting to Page or staying as Page
    if (desiredType === "page" && !selectedParent) {
      alert("Please select a parent for the page.");
      return;
    }

    // Set loading to true immediately after validation passes
    setIsSubmitting(true);
    setLoadingMessage('Preparing document update...');

    try {
      const user = auth.currentUser;
      if (!user) {
        alert("You must be logged in to save changes.");
        setIsSubmitting(false);
        return;
      }

      const token = await user.getIdToken();

      // Extract mentioned user UIDs from Quill HTML content
      const mentionedUids = (() => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(content, 'text/html');
        const spans = doc.querySelectorAll('[data-user-uid]');
        const uids = [...new Set([...spans].map(s => s.getAttribute('data-user-uid')).filter(Boolean))];
        return uids.filter(u => u !== user.uid);
      })();

      // Prepare the document data based on type
      let documentData = {};

      if (type === "space" && desiredType === "space") {
        // Space staying as space - regular update
        documentData = {
          name: name,
          description: description,
          is_public: isPublic,
          content: content,
          completed: completed,
          show_table_of_contents: showTableOfContents,
          version: documentVersion,
        };
      } else if (type === "page" && desiredType === "page") {
        // Page staying as page - update with parent info
        documentData = {
          page_name: name,
          description: description,
          is_public: isPublic,
          content: content,
          completed: completed,
          version: documentVersion,
        };

        // Add parent reference based on selected type
        if (selectedParent) {
          if (selectedParent.type === "space") {
            documentData.space_id = selectedParent.value;
            documentData.parent_page_id = null;
          } else if (selectedParent.type === "page") {
            documentData.parent_page_id = selectedParent.value;
            documentData.space_id = null;
          }
        }
      } else if (type === "space" && desiredType === "page") {
        // Converting space to page
        documentData = {
          convert_to_type: "page",
          page_name: name,
          description: description,
          is_public: isPublic,
          content: content,
          completed: completed,
          version: documentVersion,
        };

        // Parent is required for conversion to page
        if (selectedParent) {
          documentData.parent_id = selectedParent.value;
          documentData.parent_type = selectedParent.type;
        } else {
          alert("Parent selection is required when converting to a page");
          setIsSubmitting(false);
          return;
        }
      } else if (type === "page" && desiredType === "space") {
        // Converting page to space
        documentData = {
          convert_to_type: "space",
          name: name,
          description: description,
          is_public: isPublic,
          content: content,
          completed: completed,
          show_table_of_contents: showTableOfContents,
          version: documentVersion,
        };
      }

      // Attach mentioned UIDs so the backend can create notifications
      documentData.mentionedUids = mentionedUids;

      // Choose the correct endpoint based on current document type
      const endpoint =
        type === "space"
          ? `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/spaces/${id}/update/`
          : `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/pages/${id}/update/`;

      setLoadingMessage('Saving document changes...');

      // Determine if we need to use FormData (for file uploads or if attachment changes)
      const hasAttachmentChanges = attachmentFile || (currentAttachment === null && document.attachment);
      let response;

      if (hasAttachmentChanges || attachmentFile) {
        // Use FormData for file upload or attachment changes
        const formData = new FormData();
        
        // Add all document data fields to FormData
        Object.keys(documentData).forEach(key => {
          if (documentData[key] !== null && documentData[key] !== undefined) {
            const val = documentData[key];
            formData.append(key, Array.isArray(val) ? JSON.stringify(val) : val);
          }
        });
        
        // Add attachment data
        if (attachmentFile) {
          formData.append('attachment', attachmentFile);
          formData.append('attachmentDescription', attachmentDescription);
        } else if (currentAttachment === null && document.attachment) {
          // Mark for removal
          formData.append('removeAttachment', 'true');
        }
        
        response = await fetch(endpoint, {
          method: "PUT",
          headers: {
            "auth-Token": `Bearer ${token}`,
              "Authorization": `${token}`,
            "Requesting-User-Uid": user.uid,
          },
          body: formData,
        });
      } else {
        // Use regular JSON request
        response = await fetch(endpoint, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "auth-Token": `Bearer ${token}`,
              "Authorization": `${token}`,
            "Requesting-User-Uid": user.uid,
          },
          body: JSON.stringify(documentData),
        });
      }

      if (response.ok) {
        setLoadingMessage('Document saved successfully! Redirecting...');
        const data = await response.json();
        setHasUnsavedChanges(false);
        releaseGuard();

        // Handle navigation based on whether conversion happened
        if (type !== desiredType) {
          const newId = data.space_id || data.page_id;
          navigate(`/workspace/${customDomain}/${desiredType}/${newId}`);
        } else {
          navigate(`/workspace/${customDomain}/${type}/${id}`);
        }
      } else if (response.status === 409) {
        // Fetch the latest document content
        try {
          const latestResponse = await fetch(
            type === "space"
              ? `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/spaces/${id}/`
              : `https://bluebirddocumentationadmin.pythonanywhere.com/workspaces/${workspace.id}/pages/${id}/`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                "auth-Token": `Bearer ${token}`,
              "Authorization": `${token}`,
                "Requesting-User-Uid": user.uid,
              },
            }
          );

          if (latestResponse.ok) {
            setIsSubmitting(false); // Stop loading before showing conflict resolver
            alert(
              "There appears to be a conflict between your current changes and changes made by another user while editing this document. Please resolve them and submit again."
            );
            const latestData = await latestResponse.json();
            setLatestContent(latestData.content);
            setDocumentVersion(latestData.version);
            setShowConflictResolver(true);
          } else {
            throw new Error("Failed to fetch the latest version of the document.");
          }
        } catch (error) {
          console.error("Error fetching latest document version:", error);
          alert(`Error fetching latest document version: ${error.message}`);
          setIsSubmitting(false);
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update document");
      }
    } catch (error) {
      console.error("Error updating document:", error);
      alert(`Failed to update document: ${error.message}`);
      setIsSubmitting(false); // Stop loading on error
    }
  };

    const loggedOutOptions = [
        {
            label: "Login",
            icon: <i className="fas fa-sign-in-alt"></i>,
            url: "/login",
            onClick: () => navigate("/login"),
        },
        {
            label: "Register",
            icon: <i className="fas fa-user-plus"></i>,
            url: "/register",
            onClick: () => navigate("/register"),
        },
        {
            label: "Try My Notebook",
            icon: <i className="fas fa-book"></i>,
            url: "/my-notebook",
            onClick: () => navigate("/my-notebook"),
        },
        {
            label: "Visit YouTube Channel",
            icon: <i className="fab fa-youtube"></i>,
            url: "https://www.youtube.com/@schoolofintology4062",
            onClick: () => window.open("https://www.youtube.com/@schoolofintology4062", "_blank", "noopener,noreferrer"),
        },
        {
            label: "Join Discord",
            icon: <i className="fab fa-discord"></i>,
            url: "https://discord.gg/DYkESHwgVA",
            onClick: () => window.open("https://discord.gg/DYkESHwgVA", "_blank", "noopener,noreferrer"),
        },
    ];

  const loggedInOptions = [
      {
        label: "Submit Edits",
        icon: <i className="fas fa-paper-plane"></i>,
        onClick: handleSubmit,
      },
      {
        label: "Home",
        icon: <i className="fas fa-home"></i>,
        url: "/",
        onClick: () => window.open("/", "_blank", "noopener,noreferrer"),
      },
      {
        label: "Your Documents",
        url: "/documentation",
        onClick: () => window.open("/documentation", "_blank", "noopener,noreferrer"),
      },
      {
        label: "Your Walkthroughs",
        url: "/walkthroughs",
        onClick: () => window.open("/walkthroughs", "_blank", "noopener,noreferrer"),
      },
      {
        label: "Your Notebook",
        url: "/my-notebook",
        onClick: () => window.open("/my-notebook", "_blank", "noopener,noreferrer"),
      },
      {
        label: "Your Workspace",
        url: "/workspaces",
        onClick: () => window.open("/workspaces", "_blank", "noopener,noreferrer"),
      },
      {
        label: "Account Settings",
        url: `/account/${userUid}`,
        onClick: () => window.open(`/account/${userUid}`, "_blank", "noopener,noreferrer"),
      },
    ];

    const menuOptions = userUid ? loggedInOptions : loggedOutOptions;
    const { showContextMenu, ContextMenuComponent, handleContextMenu } = useContextMenu(menuOptions);

  if (authLoading || (userUid && loading)) {
    return (
        <>
            <div className="loading-animation-div" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '40vh' }}>
                <div className="coffee-cup-loader">
                    <div className="cup">
                        <div className="cup-body"></div>
                        <div className="cup-handle"></div>
                        <div className="cup-base"></div>
                        <div className="cup-shadow"></div>
                        <div className="steam steam1"></div>
                        <div className="steam steam2"></div>
                        <div className="steam steam3"></div>
                    </div>
                </div>
                <p style={{ color: '#5B92E5', fontSize: '1.1rem', marginTop: 16 }}>{loadingMessage}</p>
            </div>
        </>
    );
  }

  if (loading) {
    return (
        <>
            <div className="loading-animation-div" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '40vh' }}>
                <div className="coffee-cup-loader">
                    <div className="cup">
                        <div className="cup-body"></div>
                        <div className="cup-handle"></div>
                        <div className="cup-base"></div>
                        <div className="cup-shadow"></div>
                        <div className="steam steam1"></div>
                        <div className="steam steam2"></div>
                        <div className="steam steam3"></div>
                    </div>
                </div>
                <p style={{ color: '#5B92E5', fontSize: '1.1rem', marginTop: 16 }}>{loadingMessage}</p>
            </div>
        </>
    );
  }

  // Add this right after the initial loading checks
    if (isSubmitting) {
    return (
        <>
            <div className="loading-animation-div" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '40vh' }}>
                <div className="coffee-cup-loader">
                    <div className="cup">
                        <div className="cup-body"></div>
                        <div className="cup-handle"></div>
                        <div className="cup-base"></div>
                        <div className="cup-shadow"></div>
                        <div className="steam steam1"></div>
                        <div className="steam steam2"></div>
                        <div className="steam steam3"></div>
                    </div>
                </div>
                <p style={{ color: '#5B92E5', fontSize: '1.1rem', marginTop: 16 }}>{loadingMessage || 'Submitting...'}</p>
            </div>
        </>
    );
    }

  if (error) {
    return (
      <div 
        className="DocumentationMainPage__div DocEditor DocEditor--ws"
        onContextMenu={handleContextMenu}
    >
        <div className="DocumentationMainPage__main-content">
          <p className="large">Oops! Something went wrong!</p>
          <p className="medium">{error}</p>
          <button onClick={() => navigate(-1)} className="ConflictResolver__button">
            Go Back
          </button>
        </div>
        {enhancedRightClickEnabled && ContextMenuComponent}
      </div>
    );
  }

  if (!auth.currentUser) {
    return (
      <div 
        className="DocumentationMainPage__div DocEditor DocEditor--ws"
        onContextMenu={handleContextMenu}
    >
        <div className="DocumentationMainPage__main-content">
          <p className="large">Oops! Something went wrong!</p>
          <p className="medium">
            It looks like you're not logged in. Please log in to view this
            document.
          </p>
          <button onClick={() => navigate("/login")} className="ConflictResolver__button">
            Log In
          </button>
        </div>
        {enhancedRightClickEnabled && ContextMenuComponent}
      </div>
    );
  }

  return (
    <div 
        className="TeamWorkspaceMainPage__div DocEditor DocEditor--ws"
        onContextMenu={handleContextMenu}
    >
      <WorkspaceSidebar workspaceId={workspace.id} workspaceTheme={workspaceTheme} />

      <div className="TeamWorkspaceMainPage__main-content">
        <div className="EditDocument__header-section">
          <h2>Edit {type === "space" ? "Space" : "Page"}</h2>
          <button 
            style={{ marginTop: "2px" }} 
            className="Account__navigation-buttons"
            onClick={handleHistoryButtonClick}
          >
            <i className="fa-solid fa-clock-rotate-left"></i> History
          </button>
        </div>

        {!canEdit ? (
          <div className="unauthorized-message">
            <p>You don't have permission to edit this document.</p>
            <button onClick={() => navigate(-1)} className="ConflictResolver__button">
              Go Back
            </button>
          </div>
        ) : (
          <>
          <div className="Create__required-fields-div">
            {/* Document properties section */}
            <div className="Create__form-group Create__required-field-margin-top">
              <label className="Create__required-field">Name: </label>
              <input
                type="text"
                id="name"
                className="Create__input-short-field Create__required-field-select"
                value={name}
                onChange={handleNameChange}
                maxLength={type === "space" ? 100 : 150}
                placeholder={`Max ${type === "space" ? 100 : 150} characters`}
                required
              />
            </div>

            <div className="Create__form-group">
              <label className="Create__required-field">Document Type: </label>
              <select
                className="form-select Create__input Create__required-field-select"
                value={desiredType}
                onChange={(e) => {
                  setDesiredType(e.target.value);
                  setHasUnsavedChanges(true);
                }}
                disabled={!canEdit}
              >
                <option value="space">Space</option>
                <option value="page">Page</option>
              </select>
            </div>

            {/* Add this after the document type selector */}
            {desiredType === "page" && type === "space" && (
              <div className="Create__form-group">
                <label style={{ paddingTop: "3px" }} className="Create__required-field">
                  Parent Space or Page:{" "}
                </label>
                <Select
                  options={allDocumentation.filter(
                    (doc) => doc.value !== parseInt(id)
                  )} // Filter out self
                  value={allDocumentation.find(
                    (option) =>
                      option.value ===
                      (selectedParent ? selectedParent.value : null)
                  )}
                  onChange={(option) => {
                    setSelectedParent(
                      option
                        ? {
                            value: option.value,
                            type: option.type,
                          }
                        : null
                    );
                    setHasUnsavedChanges(true);
                  }}
                  placeholder="Select a parent (required)"
                  isSearchable={true}
                  isDisabled={!canEdit}
                  className={
                    type === "space" &&
                    desiredType === "page" &&
                    !selectedParent
                      ? "select-error"
                      : ""
                  }
                />
              </div>
            )}

            {/* For regular pages that are staying as pages, still show parent selection */}
            {desiredType === "page" && type === "page" && (
              <div className="Create__form-group">
                <label style={{ paddingTop: "3px" }} className="Create__required-field">
                  Parent Space or Page:{" "}
                </label>
                <Select
                  options={allDocumentation.filter(
                    (doc) => doc.value !== parseInt(id)
                  )} // Filter out self
                  value={allDocumentation.find(
                    (option) =>
                      option.value ===
                      (selectedParent ? selectedParent.value : null)
                  )}
                  onChange={(option) => {
                    setSelectedParent(
                      option
                        ? {
                            value: option.value,
                            type: option.type,
                          }
                        : null
                    );
                    setHasUnsavedChanges(true);
                  }}
                  placeholder="Select a parent (required)"
                  isSearchable={true}
                  isDisabled={!canEdit}
                />
              </div>
            )}

            </div>

            <div className="Create__form-group">
              <label>Description: </label>
              <input
                type="text"
                id="description"
                className="Create__input-full-field"
                value={description}
                onChange={handleDescriptionChange}
              />
            </div>
            <div className="Create__form-group-helper-text">
              <i
                className="fas fa-info-circle Create__info-icon"
                title="Information"
              ></i>
              <p className="Create__helper-text">
                This description is what team members will see about this {type}
                .
              </p>
            </div>

            <div className="Create__form-group">
              <label>Make this content public?</label>
              <label className="Create__switch">
                <input
                  type="checkbox"
                  checked={isPublic}
                  onChange={handlePublicToggle}
                />
                <span className="Create__slider round"></span>
              </label>
            </div>
            <div className="Create__form-group-helper-text">
              <i
                className="fas fa-info-circle Create__info-icon"
                title="Information"
              ></i>
              <p className="Create__helper-text">
                Public content is viewable by anyone that has this document's URL.
              </p>
            </div>

            <div className="Create__form-group">
              <label>Mark as completed: </label>
              <label className="Create__switch">
                <input
                  type="checkbox"
                  checked={completed}
                  onChange={handleCompletedToggle}
                />
                <span className="Create__slider round"></span>
              </label>
            </div>
            <div className="Create__form-group-helper-text">
              <i
                className="fas fa-info-circle Create__info-icon"
                title="Information"
              ></i>
              <p className="Create__helper-text">
                {type === "space" ? "Spaces" : "Pages"} marked as completed will
                be visible to all team members with appropriate permissions.
                Incomplete documents are only visible to editors and
                administrators.
              </p>
            </div>

            {desiredType === "space" && (
              <>
                <div className="Create__form-group">
                  <label>Show a table of contents on this space?</label>
                  <label className="Create__switch">
                    <input
                      type="checkbox"
                      checked={showTableOfContents}
                      onChange={(e) => {
                        setShowTableOfContents(e.target.checked);
                        setHasUnsavedChanges(true);
                      }}
                    />
                    <span className="Create__slider round"></span>
                  </label>
                </div>
                <div className="Create__form-group-helper-text">
                  <i
                    className="fas fa-info-circle Create__info-icon"
                    title="Information"
                  ></i>
                  <p className="Create__helper-text">
                    When enabled, viewers see an "In this space" section near
                    the top of the space listing the pages inside it that they
                    have access to.
                  </p>
                </div>
              </>
            )}

            {/* Attachment Section */}
            <div className="Create__form-group" style={{ marginTop: "20px" }}>
              <label>
                File Attachment (Optional)
              </label>

              {/* File input for new attachment */}
              <input
                type="file"
                id="attachment-upload"
                accept=".zip,.pdf,.jpeg,.png,.mp3,.mp4"
                onChange={handleAttachmentUpload}
                className="Create__file-input"
              />
              <button
                type="button"
                onClick={() => setCcPickerOpen(true)}
                style={{ display: 'block', marginBottom: '10px', marginRight: '12px', background: 'none', border: '1px solid #0d6efd', color: '#0d6efd', borderRadius: '5px', padding: '6px 12px', cursor: 'pointer', whiteSpace: 'nowrap', alignSelf: 'flex-start' }}
              >
                <i className="fas fa-box-open"></i> Choose from Command Center
              </button>

              {/* Show selected new attachment */}
              {attachmentFile && (
                <div className="Create__selected-file">
                  <button
                    type="button"
                    onClick={removeAttachment}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#dc3545",
                        fontSize: "16px"
                      }}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              )}
            </div>
              {/* Show current attachment if exists */}
              {currentAttachment && !attachmentFile && (
                <div className="Create__current-attachment">
                    <div className="Create__attachment-details">
                      <span className="Create__attachment-name" title={currentAttachment.file_name}>
                        <i className="fas fa-file-archive" style={{ marginRight: '8px' }}></i>
                        {truncateFileName(currentAttachment.file_name)}
                      </span>
                      <span className="Create__attachment-size" style={{ marginLeft: '10px', color: '#666', fontSize: '0.9em' }}>
                        ({formatFileSize(currentAttachment.file_size)})
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={removeCurrentAttachment}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#dc3545",
                        fontSize: "16px"
                      }}
                      title="Remove current attachment"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                </div>
              )}

              <div className="Create__form-group-helper-text" style={{ marginBottom: "60px" }}>
                <i className="fas fa-info-circle Create__info-icon" title="Information"></i>
                <p className="Create__helper-text">
                  You can attach one ZIP, PDF, JPEG, PNG, MP3, or MP4 file to your document. The file size limit is 8MB.
                  Users will be able to download this attachment when viewing the document.
                </p>
              </div>

            {/* Document-specific permissions section */}
            <div
              className="Create__form-group"
              style={{ flexDirection: "column", marginBottom: "60px" }}
            >
              <label>Document-specific permissions</label>
              <div className="Create__form-group-helper-text">
                <i
                  className="fas fa-info-circle Create__info-icon"
                  title="Information"
                ></i>
                <p className="Create__helper-text">
              These permissions are specific to this document, overriding
              workspace-level permissions. To learn more about group privlages, please visit <a 
                href="https://bluebird-documentation.com/documentation/page/Workspace%20Permissions/NGzFPNeV9GY1g6q2PFX7SfQmRqw1" 
                target="_blank" 
                rel="noopener noreferrer">this link</a>. 
                  <strong>
                    <i>
                      &nbsp;To keep existing workspace permissions in place for all
                      team members, leave this field blank.
                    </i>
                  </strong>
                </p>
              </div>

              {emails.map((member, index) => (
                <div
                  key={index}
                  className="Create__email-item"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "10px",
                  }}
                >
                  <span>{member.email}</span>
                  <select
                    value={member.role}
                    className="Create__select"
                    onChange={(e) => handleRoleChange(index, e.target.value)}
                    style={{ marginLeft: "10px", padding: "5px" }}
                  >
                    {PERMISSION_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => handleRemoveEmail(index)}
                    style={{
                      marginLeft: "10px",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    <i
                      className="fas fa-trash-alt"
                      title="Remove Team Member"
                      style={{ color: "white" }}
                    ></i>
                  </button>
                </div>
              ))}

              <div className="Create__email-input-container">
                <input
                  type="email"
                  className="Create__email-input"
                  placeholder="Enter Email Address"
                  value={currentEmail}
                  onChange={(e) => setCurrentEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddEmail()}
                />
                <button
                  onClick={handleAddEmail}
                  className="Create__add-email-btn"
                >
                  <i className="fas fa-plus"></i>
                </button>
              </div>
            </div>

            {/* External view-only sharing (BBD-60) */}
            {canManageExternalShares && (
              <div
                className="Create__form-group"
                style={{ flexDirection: "column", marginBottom: "60px" }}
              >
                <label>Share outside the workspace</label>
                <div className="Create__form-group-helper-text">
                  <i
                    className="fas fa-info-circle Create__info-icon"
                    title="Information"
                  ></i>
                  <p className="Create__helper-text">
                    People outside this workspace receive an email with a
                    private <strong>view-only</strong> link — they don't need
                    an account to open it. Unlike the permissions above,
                    changes here apply <strong>immediately</strong>: adding
                    someone sends the email right away, and removing them
                    revokes their link right away.
                  </p>
                </div>

                {externalShares.map((share) => (
                  <div
                    key={share.id}
                    className="Create__email-item"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "10px",
                    }}
                  >
                    <span>{share.email}</span>
                    <span style={{ marginLeft: "10px", opacity: 0.8, fontSize: "0.85em" }}>
                      <i className="fas fa-eye"></i> View only
                    </span>
                    <button
                      onClick={() => handleRemoveExternalShare(share)}
                      style={{
                        marginLeft: "10px",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      <i
                        className="fas fa-trash-alt"
                        title="Revoke access"
                        style={{ color: "white" }}
                      ></i>
                    </button>
                  </div>
                ))}

                <div className="Create__email-input-container">
                  <input
                    type="email"
                    className="Create__email-input"
                    placeholder="Enter external email address"
                    value={currentExternalEmail}
                    onChange={(e) => setCurrentExternalEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAddExternalShare()}
                  />
                  <button
                    onClick={handleAddExternalShare}
                    className="Create__add-email-btn"
                  >
                    <i className="fas fa-plus"></i>
                  </button>
                </div>
              </div>
            )}

            {/* Split view selector */}
            {showSplitView && (
              <div className="EditDocument__split-view-selector">
                <label>Side View Document:</label>
                <Select
                  options={allDocumentation}
                  value={allDocumentation.find(
                    (doc) => doc.value === selectedViewDoc
                  )}
                  onChange={(option) => {
                    setSelectedViewDoc(option ? option.value : null);
                    setSelectedViewDocName(option ? option.label : "");
                  }}
                  placeholder="Choose a document..."
                  isSearchable={true}
                />
              </div>
            )}

            {/* Editor section */}
            <div className="EditDocument__edit-document-container">
              <div
                className={`EditDocument__editor-section ${
                  selectedViewDoc ? "split" : ""
                }`}
              >
                <div
                  className={`EditDocument__quill-container ${
                    isFullscreen ? "fullscreen-mode" : ""
                  }`}
                >
                  {showConflictResolver ? (
                    <ConflictResolver
                      localContent={content}
                      latestContent={latestContent}
                      onResolve={(resolvedContent) => {
                        setContent(resolvedContent);
                        setShowConflictResolver(false);
                      }}
                    />
                  ) : (
                    <>
                    <div className="QuillOverrides__content-formatting">
                      <ReactQuill
                        ref={quillRef}
                        value={content}
                        onChange={handleContentChange}
                        modules={modules}
                        style={{
                          height: isFullscreen ? "calc(100vh - 50px)" : "70vh",
                        }}
                      />
                      <TaskReferenceDropdown {...taskRefProps} />
                      <UserMentionDropdown {...userMentionProps} />
                      </div>
                      <button
                        onClick={toggleFullscreen}
                        className="EditDocument__fullscreen-btn EditDocument__fullscreen-btn-corner"
                        title={
                          isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"
                        }
                        style={{ bottom: isFullscreen ? "20px" : "-18px" }}
                      >
                        <i
                          className={`fas ${
                            isFullscreen ? "fa-compress-alt" : "fa-expand-alt"
                          }`}
                        ></i>
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Side view document */}
              {selectedViewDoc && showSplitView && (
                <div className="EditDocument__view-document-section">
                  <div className="EditDocument__view-document-header">
                    <p className="medium">
                      <strong>Viewing:</strong> <i>{viewDocTitle}</i>
                    </p>
                    <button
                      onClick={() => setSelectedViewDoc(null)}
                      className="EditDocument__close-btn"
                    >
                      ✖
                    </button>
                  </div>
                  <div className="EditDocument__view-document-content">
                    <div dangerouslySetInnerHTML={{ __html: viewDocContent }} />
                  </div>
                </div>
              )}
            </div>

            <div className="EditDocument__button-container">              
              <button onClick={handleSubmit} className="submit-button">
                Submit
              </button>
            </div>
          </>
        )}
      </div>
      {enhancedRightClickEnabled && ContextMenuComponent}
      
      {/* History Modal */}
      {showHistoryModal && (
        <WorkspaceDocumentHistoryModal
          history={documentHistory}
          documentName={name}
          onClose={() => setShowHistoryModal(false)}
        />
      )}

      {templateSnippets.element}
      <CommandCenterFilePickerModal
        isOpen={ccPickerOpen}
        onClose={() => setCcPickerOpen(false)}
        onSelect={(file) => setAttachmentFile(file)}
        allowedExtensions={['.zip', '.pdf', '.jpeg', '.png', '.mp3', '.mp4']}
        maxSizeBytes={8 * 1024 * 1024}
      />
    </div>
  );
}

export default EditWorkspaceDocument;
